import { authRoutes } from "@/modules/auth/routes";
import { mainRoutes } from "@/modules/main/routes";
import { useRolloverStore } from "@/stores/rolloverStore";
import { useSchoolStore } from "@/stores/schoolStore";
import { useUserStore } from "@/stores/userStore";
import { needsRegionReauth } from "@/lib/runtime-url-resolver";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  createRouter,
  createWebHistory,
  NavigationGuardNext,
  RouteLocationNormalized,
  RouteRecordRaw,
} from "vue-router";

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    redirect: "/main",
  },
  {
    path: "/login",
    redirect: "/auth/login",
  },
  {
    path: "/main",
    name: "main",
    meta: { requiresAuth: true },
    component: () =>
      import(
        /* webpackChunkName: "main-module" */ "../modules/main/MainModule.vue"
      ),
    children: [...mainRoutes],
  },
  {
    path: "/auth",
    name: "auth",
    component: () =>
      import(
        /* webpackChunkName: "auth-module" */ "../modules/auth/AuthModule.vue"
      ),
    children: [...authRoutes],
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

const auth = getAuth();
let authStateResolved = false;

const authPromise = new Promise<void>((resolve) => {
  onAuthStateChanged(auth, (user) => {
    authStateResolved = true;
    resolve(user as any);
  });
});

const authGuard = async (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  const rolloverStore = useRolloverStore();
  const schoolStore = useSchoolStore();
  const auth = getAuth();

  if (!authStateResolved) {
    await authPromise;
  }

  // Region routing is applied only inside userStore.login(), so a session created
  // before it shipped would stay on the default cluster forever. Sign such sessions
  // out once so they come back through login(), which is the single authority on
  // routing. The marker survives the purge, so this can fire at most once per
  // browser and cannot loop.
  if (auth.currentUser !== null && needsRegionReauth()) {
    await useUserStore().forceRegionReauth();
    // A full document navigation is in flight; abandon this one.
    return;
  }

  if (to.meta.requiresAuth && auth.currentUser === null) {
    // this route requires auth, check if logged in
    // if not, redirect to login page.
    next({
      name: "login",
      // save the location we were at to come back later
      query: { redirect: to.fullPath },
    });
  }

  if (to.matched.some((record) => record.meta.requiresRolloverExecution)) {
    const learnerRolloverExecutionComplete =
      rolloverStore.rollover &&
      rolloverStore.rollover?.status.classReview == "complete" &&
      rolloverStore.rollover?.status.learnerRollover == "complete";

    if (!learnerRolloverExecutionComplete) {
      next({ name: "rollover-in-progress" });
    }
  }

  if (to.matched.some((record) => record.meta.requiresRolloverLearnersReview)) {
    const learnerReviewComplete =
      rolloverStore.rollover &&
      rolloverStore.rollover?.status.learnerRollover == "complete";

    const learnerReviewPendingJob =
      rolloverStore.rollover &&
      rolloverStore.rollover?.status.learnerRollover == "pending_job_run";

    if (learnerReviewComplete || learnerReviewPendingJob) {
      next({ name: "internal-edit-school" });
    }
  }

  if (to.matched.some((record) => record.meta.requiresRolloverClassReview)) {
    const classReviewComplete =
      rolloverStore.rollover &&
      rolloverStore.rollover?.status.classReview == "complete";

    const classReviewPendingJob =
      rolloverStore.rollover &&
      rolloverStore.rollover?.status.classReview == "pending_job_run";

    if (classReviewComplete || classReviewPendingJob) {
      next({ name: "internal-edit-school" });
    }
  }

  // REMOVED: Blocking navigation guard for school structure setup
  // Users can now navigate freely even without grade levels configured
  // The setup banner will still appear on the Admin page as a guide
  // if (
  //   auth.currentUser !== null &&
  //   to.name !== "admin" &&
  //   schoolStore.currentSchoolStructure &&
  //   schoolStore.currentSchoolStructure.gradeLevels.length < 1
  // ) {
  //   console.log("redirecting to admin");
  //   next({ name: "admin" });
  // }

  next();
};

router.beforeEach(authGuard);
// router.beforeEach(rolloverExecutedGuard);
// router.beforeEach(schoolNotSetupGuard);

export default router;
